import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Request, SCData, TabState, Touchpoint } from '../../types';
import { buildSCList, buildTouchpointSCList, PRODUCT_BUCKETS, bucketProducts, matchesCQFilter, matchesTouchpointFilter, matchesSub, getQuarterMode } from '../../utils/filter';
import { fmtG } from '../../utils/format';
import { sumUniqueByOpp, sumScTotalsGnarr, validReqAges, isReqAged, ageSeverity, formatReqAge, splitUserSCNames } from '../../utils/opportunities';
import { useRequests } from '../../context/RequestsContext';
import { fetchContributorStatus } from '../../utils/database';
import { MetricCard } from '../ui/MetricCard';
import { MedianAgeCard } from '../ui/MedianAgeCard';
import { GnarrInfluencedCard } from '../ui/GnarrInfluencedCard';
import { SectionHeader } from '../ui/SectionHeader';
import { SortableTable, type SortableColumn } from '../ui/SortableTable';
import { getRequestURL } from '../../utils/opportunities';
interface Props {
  state: TabState;
}
type RequestViewMode = 'cards' | 'table' | 'tile' | 'gallery' | 'waterfall';

// Touchpoints have no closed quarter, so in Closed mode they keep using created quarter/date (ADR 0010).
function TouchpointCreatedHint() {
  return <span className="normal-case tracking-normal font-normal text-[#8892a0] ml-1" data-id="element-828" data-mp="3vqlkay6f6gvh">
      · touchpoints filtered by created quarter / date
    </span>;
}

/* ─── Date sort helper (dates are stored as dd/mm/yy strings) ──────────── */

function ddmmyyToSortValue(entry: string | null | undefined): number {
  if (!entry) return 0;
  const p = entry.split('/');
  if (p.length !== 3) return 0;
  const [dd, mm, yy] = p.map(Number);
  return new Date(yy > 50 ? 1900 + yy : 2000 + yy, mm - 1, dd).getTime();
}

/* ─── req_products is one comma-separated string per request ───────────── */

function splitProducts(products: string | null | undefined): string[] {
  if (!products) return [];
  return products.split(',').map(p => p.trim()).filter(Boolean);
}

/* ─── SVG icons for the view switcher ──────────────────────────────────── */

function IconCards() {
  return <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-829" data-mp="3l43fgx5o9ozg">
      <rect x="1" y="1" width="6" height="6" rx="1" data-id="element-830" data-mp="3q4hfpt117umx" />
      <rect x="9" y="1" width="6" height="6" rx="1" data-id="element-831" data-mp="3u631k3vjnj99" />
      <rect x="1" y="9" width="6" height="6" rx="1" data-id="element-832" data-mp="3u4rvfxemlzez" />
      <rect x="9" y="9" width="6" height="6" rx="1" data-id="element-833" data-mp="2xg3hpd9y0ybd" />
    </svg>;
}
function IconTable() {
  return <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" data-id="element-834" data-mp="32dlph0c857hs">
      <rect x="1" y="2" width="14" height="12" rx="1.5" data-id="element-835" data-mp="36exk5k9ygiwu" />
      <line x1="1" y1="5.5" x2="15" y2="5.5" data-id="element-836" data-mp="3b6p0z33b7a9s" />
      <line x1="1" y1="9" x2="15" y2="9" data-id="element-837" data-mp="3b7id6jl1erzb" />
      <line x1="6" y1="5.5" x2="6" y2="14" data-id="element-838" data-mp="3b8jqkupizqk7" />
    </svg>;
}
function IconTile() {
  return <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-839" data-mp="2emzw8f6vprru">
      <rect x="1" y="1" width="4" height="4" rx="0.5" data-id="element-840" data-mp="2b7ev76utwsp3" />
      <rect x="6" y="1" width="4" height="4" rx="0.5" data-id="element-841" data-mp="2b63pv2lqjcvv" />
      <rect x="11" y="1" width="4" height="4" rx="0.5" data-id="element-842" data-mp="267k5v7qpcb65" />
      <rect x="1" y="6" width="4" height="4" rx="0.5" data-id="element-843" data-mp="265za5esnim6p" />
      <rect x="6" y="6" width="4" height="4" rx="0.5" data-id="element-844" data-mp="2255hy986el80" />
      <rect x="11" y="6" width="4" height="4" rx="0.5" data-id="element-845" data-mp="1x742dy9yl41a" />
      <rect x="1" y="11" width="4" height="4" rx="0.5" data-id="element-846" data-mp="1x6lwe9xhvbg6" />
      <rect x="6" y="11" width="4" height="4" rx="0.5" data-id="element-847" data-mp="1s8kgfxvd7s8x" />
      <rect x="11" y="11" width="4" height="4" rx="0.5" data-id="element-848" data-mp="1sa3wpoh0u3ap" />
    </svg>;
}
function IconGallery() {
  return <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-849" data-mp="1nfhk5hz7e4p1">
      <rect x="1" y="1" width="6" height="7" rx="1" data-id="element-850" data-mp="1eh5wnotmvn0t" />
      <rect x="9" y="1" width="6" height="7" rx="1" data-id="element-851" data-mp="1eigyvk7fkmpt" />
      <rect x="1" y="10" width="14" height="5" rx="1" data-id="element-852" data-mp="19hsyi7xbatix" />
    </svg>;
}
function IconWaterfall() {
  return <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-853" data-mp="14jhnvuz70ezn">
      <line x1="2" y1="1" x2="2" y2="15" data-id="element-854" data-mp="34lhj6r6j9ws7" />
      <line x1="6" y1="1" x2="6" y2="15" data-id="element-855" data-mp="39jj1h9w4xs2m" />
      <line x1="10" y1="1" x2="10" y2="15" data-id="element-856" data-mp="39j9apk39ngvv" />
      <line x1="14" y1="1" x2="14" y2="15" data-id="element-857" data-mp="2uuwb8d4yzfwm" />
      <rect x="1" y="3" width="3" height="2" rx="0.5" fill="currentColor" opacity="0.3" data-id="element-858" data-mp="2uu3d20k2tyq3" />
      <rect x="5" y="6" width="3" height="3" rx="0.5" fill="currentColor" opacity="0.3" data-id="element-859" data-mp="2zsxuoz6b5h8m" />
      <rect x="9" y="2" width="3" height="4" rx="0.5" fill="currentColor" opacity="0.3" data-id="element-860" data-mp="2ltygklgf8kqp" />
      <rect x="13" y="8" width="2" height="3" rx="0.5" fill="currentColor" opacity="0.3" data-id="element-861" data-mp="2pwbbsfxjl098" />
    </svg>;
}
const VIEW_OPTIONS: {
  mode: RequestViewMode;
  label: string;
  icon: React.FC;
}[] = [{
  mode: 'cards',
  label: 'Cards',
  icon: IconCards
}, {
  mode: 'table',
  label: 'Table',
  icon: IconTable
}, {
  mode: 'tile',
  label: 'Tile',
  icon: IconTile
}, {
  mode: 'gallery',
  label: 'Gallery',
  icon: IconGallery
}, {
  mode: 'waterfall',
  label: 'Waterfall',
  icon: IconWaterfall
}];

/* ─── Shared request-name link with icon ───────────────────────────────── */

const LINK_STYLE: React.CSSProperties = {
  color: '#1d4ed8',
  textDecoration: 'none'
};
function RequestLink({
  r,
  maxLen = 35
}: {
  r: Request;
  maxLen?: number;
}) {
  return <a href={getRequestURL(r)} target="_blank" rel="noopener noreferrer" style={LINK_STYLE} title={r.req_name} data-id="element-862" data-mp="29dfacri7583s">
      <svg viewBox="0 0 12 12" fill="none" stroke="#1d4ed8" strokeWidth="1.4" strokeLinecap="round" style={{
      display: 'inline-block',
      width: 9,
      height: 9,
      marginRight: 3,
      verticalAlign: 'middle',
      position: 'relative',
      top: -1
    }} data-id="element-863" data-mp="2ic0pecogm4zt">
        <path d="M5 1.5H3.5A2 2 0 0 0 1.5 3.5v5A2 2 0 0 0 3.5 10.5h5a2 2 0 0 0 2-2V8.5" data-id="element-864" data-mp="2s95fgnlfeklw" />
        <path d="M7.5 1.5h3v3" data-id="element-865" data-mp="2ncf0y4p9rx2l" />
        <path d="M5.5 6.5 10.5 1.5" data-id="element-866" data-mp="2nc5a6ewehlvu" />
      </svg>
      {r.req_name.substring(0, maxLen)}
    </a>;
}

/* ─── Wrapping cell for comma-separated SC names (td defaults to nowrap + clip) ─── */

const WRAP_CELL_STYLE: React.CSSProperties = {
  display: 'block',
  whiteSpace: 'normal',
  overflowWrap: 'anywhere'
};
function ScNamesCell({
  names
}: {
  names: string;
}) {
  return <span title={names} style={WRAP_CELL_STYLE} data-id="element-867" data-mp="1mo4nvjtw06hi">{names}</span>;
}

/* ─── View switcher bar ────────────────────────────────────────────────── */

function ViewSwitcher({
  active,
  onChange
}: {
  active: RequestViewMode;
  onChange: (m: RequestViewMode) => void;
}) {
  return <div className="view-switcher" data-id="element-868" data-mp="21kvr646om2ce">
      {VIEW_OPTIONS.map(opt => <button key={opt.mode} className={active === opt.mode ? 'active' : ''} onClick={() => onChange(opt.mode)} title={opt.label} data-id="element-869" data-mp="21ijarvgko01g">
          <opt.icon data-id="element-870" data-mp="2adfrd92mgezc" />
          {opt.label}
        </button>)}
    </div>;
}

/* ─── Age bucket helper for waterfall ──────────────────────────────────── */

function ageBucket(age: number | null): string {
  if (age == null) return 'Unknown';
  if (age <= 7) return '0–7 days';
  if (age <= 14) return '8–14 days';
  if (age < 30) return '15–29 days';
  return '30+ days';
}
const AGE_BUCKETS = ['0–7 days', '8–14 days', '15–29 days', '30+ days'];
function groupByAgeBucket(requests: Request[]): Record<string, Request[]> {
  const groups: Record<string, Request[]> = {};
  for (const b of AGE_BUCKETS) groups[b] = [];
  for (const r of requests) {
    const b = ageBucket(r.req_age);
    if (!groups[b]) groups[b] = [];
    groups[b].push(r);
  }
  return groups;
}

/* ─── Display-only age for in-progress deals ──────────────────────────── */

/* ─── Individual view renderers ────────────────────────────────────────── */

function TableView({
  requests
}: {
  requests: Request[];
}) {
  const reqColumns: SortableColumn<Request>[] = [{
    key: 'opp_id',
    label: 'ID',
    width: '9%',
    sortValue: r => r.opp_id,
    render: r => <span style={{
      color: '#8892a0',
      fontSize: 9
    }} data-id="element-871" data-mp="1f3ama7mjh0xr">{r.opp_id}</span>
  }, {
    key: 'acc_name',
    label: 'Account',
    width: '16%',
    sortValue: r => r.acc_name,
    render: r => <span title={r.acc_name} data-id="element-872" data-mp="1aar7x8g121rw">{r.acc_name.substring(0, 24)}</span>
  }, {
    key: 'req_name',
    label: 'Request',
    width: '18%',
    sortValue: r => r.req_name,
    render: r => <span title={r.req_name} data-id="element-873" data-mp="ngsrfyi3br0t"><RequestLink r={r} maxLen={30} data-id="element-874" data-mp="nham4qp3tx6m" /></span>
  }, {
    key: 'req_age',
    label: 'Age',
    width: '8%',
    sortValue: r => r.req_age ?? -1,
    render: r => <span className={ageSeverity(r.req_age) === 'high' ? 'c-red' : ageSeverity(r.req_age) === 'medium' ? 'c-gold' : ''} style={{
      fontWeight: ageSeverity(r.req_age) === 'high' ? 600 : 400
    }} data-id="element-875" data-mp="emw78p8hkoo1">
          {formatReqAge(r.req_age)}
        </span>
  }, {
    key: 'req_entry',
    label: 'Date',
    width: '8%',
    sortValue: r => ddmmyyToSortValue(r.req_entry),
    render: r => <span style={{
      fontSize: 9,
      color: '#667085'
    }} data-id="element-876" data-mp="xe6v0xix0lbp">{r.req_entry}</span>
  }, {
    key: 'opp_gnarr',
    label: 'GNARR',
    width: '9%',
    sortValue: r => r.opp_gnarr,
    render: r => <span data-id="element-877" data-mp="3d404b6v9w7mq">{fmtG(r.opp_gnarr)}</span>
  }, {
    key: 'opp_close_q',
    label: 'Close Q',
    width: '9%',
    sortValue: r => r.opp_close_q || '',
    render: r => <span data-id="element-878" data-mp="3akdhidohj1j5">{r.opp_close_q}</span>
  }, {
    key: 'req_offering',
    label: 'Offering',
    width: '13%',
    sortValue: r => r.req_offering || '',
    render: r => <span className="badge b-blue" style={{
      fontSize: 8
    }} data-id="element-879" data-mp="35liqiogd66ny">{r.req_offering?.substring(0, 18)}</span>
  }, {
    key: 'req_sub_region',
    label: 'Sub Region',
    width: '10%',
    sortValue: r => r.req_sub_region || '',
    render: r => <span className="badge b-gray" style={{
      fontSize: 8
    }} data-id="element-880" data-mp="3eknq7y3nw61p">{r.req_sub_region}</span>
  }];
  return <SortableTable columns={reqColumns} data={requests} rowKey={r => `${r.req_id}|${r.user_sc_name}`} className="deal-table" data-id="element-881" data-mp="2qujj3gr9lo5y" />;
}
function TileView({
  requests
}: {
  requests: Request[];
}) {
  return <div className="deal-tile-grid" data-id="element-882" data-mp="30ojzwfvwshui">
      {requests.map(r => <div key={`${r.req_id}|${r.user_sc_name}`} className="deal-tile" title={`${r.acc_name}\n${r.req_name}\nAge: ${formatReqAge(r.req_age)}\nGNARR: ${fmtG(r.opp_gnarr)}\nDate: ${r.req_entry}`} data-id="element-883" data-mp="2vufqzr331zqs">
          <div className="dt-id" data-id="element-884" data-mp="236s8qipabl7b">{r.opp_id}</div>
          <div className="dt-acct" data-id="element-885" data-mp="9m7e7mgzvqd1">{r.acc_name.substring(0, 14)}</div>
          <div style={{
        fontSize: 8,
        marginTop: 2,
        lineHeight: 1.2
      }} data-id="element-886" data-mp="9kd1gvfvn4k1">
            <RequestLink r={r} maxLen={16} data-id="element-887" data-mp="4onysj8k97ji" />
          </div>
          <div style={{
        fontSize: 8,
        color: '#667085',
        marginTop: 2
      }} data-id="element-888" data-mp="3vte2khzvgb9a">{r.req_entry}</div>
          <span className={`dt-age ${ageSeverity(r.req_age) === 'high' ? 'b-red' : ageSeverity(r.req_age) === 'medium' ? 'b-amber' : 'b-gray'}`} data-id="element-889" data-mp="3rs2g2lcao65b">
            {formatReqAge(r.req_age)}
          </span>
        </div>)}
    </div>;
}
function GalleryView({
  requests
}: {
  requests: Request[];
}) {
  return <div className="deal-gallery" data-id="element-890" data-mp="3d2mtlhjsqmvf">
      {requests.map(r => <div key={`${r.req_id}|${r.user_sc_name}`} className="deal-gallery-card" data-id="element-891" data-mp="3d13a7i2ufvpf">
          <div className="dg-id" data-id="element-892" data-mp="385wg91zdm5dp">{r.opp_id}</div>
          <div className="dg-title" data-id="element-893" data-mp="342zs6vr2hh2d">{r.acc_name}</div>
          <div style={{
        fontSize: 10,
        marginBottom: 4
      }} data-id="element-894" data-mp="341om2pa5fx83">
            <RequestLink r={r} maxLen={40} data-id="element-895" data-mp="2z350wr3dqpgs" />
          </div>
          <div style={{
        fontSize: 9,
        color: '#667085',
        marginBottom: 4
      }} data-id="element-896" data-mp="2z5raew9tzf1n">{r.req_entry}</div>
          <div className="dg-meta" data-id="element-897" data-mp="1enp1sowzki7z">
            <span className={`badge ${ageSeverity(r.req_age) === 'high' ? 'b-red' : ageSeverity(r.req_age) === 'medium' ? 'b-amber' : 'b-gray'}`} data-id="element-898" data-mp="1jjnx6wgqf0hw">{formatReqAge(r.req_age)}</span>
            <span className="badge b-blue" data-id="element-899" data-mp="1sl5cwdq89062">{fmtG(r.opp_gnarr)}</span>
            <span className="badge b-purple" style={{
          fontSize: 8
        }} data-id="element-900" data-mp="1skc99lmkeqs5">{r.opp_close_q}</span>
            {isReqAged(r) && <span className="badge b-red" data-id="element-901" data-mp="vss0bkxf5f3s">⚠ aged</span>}
          </div>
          <div className="dg-footer" data-id="element-902" data-mp="zwzongzyst1h">
            {r.req_sub_region} · {r.req_offering?.substring(0, 26)}
          </div>
        </div>)}
    </div>;
}
function WaterfallView({
  requests
}: {
  requests: Request[];
}) {
  const groups = groupByAgeBucket(requests);
  return <div className="deal-waterfall" data-id="element-903" data-mp="c5u9z55rg6sr">
      {AGE_BUCKETS.map(bucket => {
      const items = groups[bucket] || [];
      return <div key={bucket} className="deal-waterfall-lane" data-id="element-904" data-mp="h3vrvmrga02n">
            <div className="deal-waterfall-lane-header" data-id="element-905" data-mp="1kfugjx1r50ja">
              <span data-id="element-906" data-mp="1fhsynfg2b79e">{bucket}</span>
              <span className="lane-count" data-id="element-907" data-mp="1fikriii9yqvz">{items.length}</span>
            </div>
            <div className="deal-waterfall-lane-body" data-id="element-908" data-mp="1t9pmh7ap6uzz">
              {items.length === 0 && <div style={{
            fontSize: 9,
            color: '#d1d5db',
            textAlign: 'center',
            padding: '12px 4px',
            fontStyle: 'italic'
          }} data-id="element-909" data-mp="1p7ujlyd4c9k3">none</div>}
              {items.map(d => <div key={`${d.req_id}|${d.user_sc_name}`} className="deal-waterfall-card" data-id="element-910" data-mp="wix608jur0xo">
                  <div className="dw-id" data-id="element-911" data-mp="wl9t104gw1hl">{d.opp_id}</div>
                  <div className="dw-acct" title={d.acc_name} data-id="element-912" data-mp="1ajhn90a4cop2">{d.acc_name.substring(0, 18)}</div>
                  <div style={{
              fontSize: 8,
              marginTop: 2,
              lineHeight: 1.2
            }} data-id="element-913" data-mp="15iu0iqsxau0n">
                    <RequestLink r={d} maxLen={20} data-id="element-914" data-mp="2lx28ngcy50sr" />
                  </div>
                  <div style={{
              fontSize: 8,
              color: '#667085',
              marginTop: 2
            }} data-id="element-915" data-mp="2gz0qqyr9b7iv">{d.req_entry}</div>
                  <div className="dw-tags" data-id="element-916" data-mp="2pg11x43y4sp8">
                    <span className={`badge ${ageSeverity(d.req_age) === 'high' ? 'b-red' : ageSeverity(d.req_age) === 'medium' ? 'b-amber' : 'b-gray'}`} style={{
                fontSize: 8
              }} data-id="element-917" data-mp="2pezsf42kwe9m">{formatReqAge(d.req_age)}</span>
                    <span className="badge b-blue" style={{
                fontSize: 8
              }} data-id="element-918" data-mp="2fj682yimbgha">{fmtG(d.opp_gnarr)}</span>
                  </div>
                </div>)}
            </div>
          </div>;
    })}
    </div>;
}

/* ─── Dispatch view ────────────────────────────────────────────────────── */

function DealExpandedView({
  requests,
  mode
}: {
  requests: Request[];
  mode: RequestViewMode;
}) {
  const [dSearchId, setDSearchId] = useState('');
  const [dSearchAccount, setDSearchAccount] = useState('');
  const [dSearchRequest, setDSearchRequest] = useState('');
  const [dAgeMin, setDAgeMin] = useState('');
  const [dAgeMax, setDAgeMax] = useState('');
  const [dEntryFrom, setDEntryFrom] = useState('');
  const [dEntryTo, setDEntryTo] = useState('');
  const [dGnarrMin, setDGnarrMin] = useState('');
  const [dGnarrMax, setDGnarrMax] = useState('');
  const [dCloseQFilter, setDCloseQFilter] = useState<string[]>([]);
  const [dCloseQDropdownOpen, setDCloseQDropdownOpen] = useState(false);
  const [dOfferingFilter, setDOfferingFilter] = useState<string[]>([]);
  const [dOfferingDropdownOpen, setDOfferingDropdownOpen] = useState(false);
  const [dSubFilter, setDSubFilter] = useState<string[]>([]);
  const [dSubDropdownOpen, setDSubDropdownOpen] = useState(false);
  const dCloseQs = [...new Set(requests.map(r => r.opp_close_q).filter((v): v is string => Boolean(v)))].sort();
  const dOfferings = [...new Set(requests.map(r => r.req_offering).filter((v): v is string => Boolean(v)))].sort();
  const dSubs = [...new Set(requests.map(r => r.req_sub_region).filter((v): v is string => Boolean(v)))].sort();
  const parseEntry = (entry: string): Date | null => {
    if (!entry) return null;
    const p = entry.split('/');
    if (p.length !== 3) return null;
    const [dd, mm, yy] = p.map(Number);
    return new Date(yy > 50 ? 1900 + yy : 2000 + yy, mm - 1, dd);
  };
  const filtered = requests.filter(r => {
    if (dSearchId && !r.opp_id.toLowerCase().includes(dSearchId.toLowerCase())) return false;
    if (dSearchAccount && !r.acc_name.toLowerCase().includes(dSearchAccount.toLowerCase())) return false;
    if (dSearchRequest && !r.req_name.toLowerCase().includes(dSearchRequest.toLowerCase())) return false;
    if (dAgeMin || dAgeMax) {
      const age = r.req_age;
      if (age === null) return false;
      if (dAgeMin && age < Number(dAgeMin)) return false;
      if (dAgeMax && age > Number(dAgeMax)) return false;
    }
    if (dEntryFrom || dEntryTo) {
      const dt = parseEntry(r.req_entry);
      if (dt) {
        if (dEntryFrom) {
          const f = new Date(dEntryFrom);
          if (dt < f) return false;
        }
        if (dEntryTo) {
          const t = new Date(dEntryTo);
          if (dt > t) return false;
        }
      }
    }
    if (dGnarrMin && r.opp_gnarr < Number(dGnarrMin)) return false;
    if (dGnarrMax && r.opp_gnarr > Number(dGnarrMax)) return false;
    if (dCloseQFilter.length > 0 && !dCloseQFilter.includes(r.opp_close_q)) return false;
    if (dOfferingFilter.length > 0 && !dOfferingFilter.includes(r.req_offering)) return false;
    if (dSubFilter.length > 0 && !dSubFilter.includes(r.req_sub_region)) return false;
    return true;
  });
  if (requests.length === 0) {
    return <div style={{
      fontSize: 10,
      color: '#8892a0',
      padding: '8px 0',
      fontStyle: 'italic'
    }} data-id="element-919" data-mp="32hafmofc1a2x">No in-progress requests.</div>;
  }
  const inputStyle = {
    fontSize: 10,
    padding: '4px 8px',
    border: '1px solid #d0d5dd',
    borderRadius: 5,
    outline: 'none',
    width: 110,
    color: '#344054',
    background: '#fff'
  };
  const dateStyle = {
    fontSize: 10,
    padding: '3px 6px',
    border: '1px solid #d0d5dd',
    borderRadius: 5,
    outline: 'none',
    color: '#344054',
    background: '#fff'
  };
  const numStyle = {
    fontSize: 10,
    padding: '4px 6px',
    border: '1px solid #d0d5dd',
    borderRadius: 5,
    outline: 'none',
    width: 60,
    color: '#344054',
    background: '#fff'
  };
  const btnStyle = {
    fontSize: 10,
    padding: '4px 8px',
    border: '1px solid #d0d5dd',
    borderRadius: 5,
    outline: 'none',
    color: '#344054',
    background: '#fff',
    cursor: 'pointer' as const,
    display: 'flex' as const,
    alignItems: 'center' as const,
    gap: 4
  };
  const ddStyle = {
    position: 'absolute' as const,
    top: '100%',
    left: 0,
    marginTop: 4,
    zIndex: 50,
    background: '#fff',
    border: '1px solid #d0d5dd',
    borderRadius: 5,
    boxShadow: '0 2px 6px rgba(0,0,0,.12)',
    minWidth: 140,
    padding: '4px 0'
  };
  const renderDropdown = (items: string[], selected: string[], toggle: (v: string[]) => void, open: boolean, setOpen: (v: boolean) => void) => <div style={{
    position: 'relative'
  }} data-id="element-920" data-mp="fbpeuq1635qx">
      <button style={btnStyle} onClick={() => setOpen(!open)} data-id="element-921" data-mp="fciqo5ezglfx">
        {selected.length > 0 ? `${selected.length} selected` : `${items[0] === dCloseQs[0] ? 'Close Q' : items[0] === dOfferings[0] ? 'Offering' : 'Sub'} ▾`}
      </button>
      {open && <div style={ddStyle} data-id="element-922" data-mp="3i82ze44or41o">
          <div onClick={() => toggle(selected.length === items.length ? [] : [...items])} style={{
        padding: '4px 10px',
        fontSize: 10,
        cursor: 'pointer',
        color: '#667085',
        borderBottom: '1px solid #eaecf0'
      }} data-id="element-923" data-mp="3i8cu250odzdp">
            {selected.length === items.length ? 'Deselect all' : 'Select all'}
          </div>
          {items.map(item => <label key={item} style={{
        padding: '4px 10px',
        fontSize: 10,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: 6
      }} onClick={e => e.stopPropagation()} data-id="element-924" data-mp="2zj66o4oxsjlk">
              <input type="checkbox" checked={selected.includes(item)} onChange={() => toggle(selected.includes(item) ? selected.filter(x => x !== item) : [...selected, item])} data-id="element-925" data-mp="2pjxcbaq5t2yo" />
              {item}
            </label>)}
        </div>}
    </div>;
  const toComparable = (entry: string) => {
    if (!entry) return 0;
    const [dd, mm, yy] = entry.split('/');
    return +yy * 10000 + +mm * 100 + +dd;
  };
  const sorted = [...filtered].sort((a, b) => toComparable(b.req_entry) - toComparable(a.req_entry));
  const filterBar = <div style={{
    padding: '6px 0',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    flexWrap: 'wrap'
  }} data-id="element-926" data-mp="26lvl77zq4mq7">
      <input type="text" placeholder="ID" value={dSearchId} onChange={e => setDSearchId(e.target.value)} style={inputStyle} data-id="element-927" data-mp="2fgilxqug0aw4" />
      <input type="text" placeholder="Account" value={dSearchAccount} onChange={e => setDSearchAccount(e.target.value)} style={inputStyle} data-id="element-928" data-mp="1ir18gpp7xkjv" />
      <input type="text" placeholder="Request" value={dSearchRequest} onChange={e => setDSearchRequest(e.target.value)} style={{
      ...inputStyle,
      width: 130
    }} data-id="element-929" data-mp="1npmamuo16eay" />
      <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 3,
      fontSize: 10,
      color: '#667085'
    }} data-id="element-930" data-mp="1wqao9o9iicnb">
        <span data-id="element-931" data-mp="336w48pn86vdi">Age</span>
        <input type="number" placeholder="min" value={dAgeMin} onChange={e => setDAgeMin(e.target.value)} style={numStyle} data-id="element-932" data-mp="335l18s1ltrng" />
        <span data-id="element-933" data-mp="387aca7otu5bj">–</span>
        <input type="number" placeholder="max" value={dAgeMax} onChange={e => setDAgeMax(e.target.value)} style={numStyle} data-id="element-934" data-mp="39m6rpbwmttg2" />
      </div>
      <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 3,
      fontSize: 10,
      color: '#667085'
    }} data-id="element-935" data-mp="3ejylyckr1rnf">
        <span data-id="element-936" data-mp="3jd1fwltfv345">Entry</span>
        <input type="date" value={dEntryFrom} onChange={e => setDEntryFrom(e.target.value)} style={dateStyle} data-id="element-937" data-mp="3jci4zp27hezm" />
        <span data-id="element-938" data-mp="3ocnt4v99ojcy">–</span>
        <input type="date" value={dEntryTo} onChange={e => setDEntryTo(e.target.value)} style={dateStyle} data-id="element-939" data-mp="2qnazu788xr5e" />
      </div>
      <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 3,
      fontSize: 10,
      color: '#667085'
    }} data-id="element-940" data-mp="2vlciws5o9qgv">
        <span data-id="element-941" data-mp="30kz35ljx46z6">GNARR</span>
        <input type="number" placeholder="min" value={dGnarrMin} onChange={e => setDGnarrMin(e.target.value)} style={numStyle} data-id="element-942" data-mp="30jnt53zvq2zm" />
        <span data-id="element-943" data-mp="35hz2zeq6cdhu">–</span>
        <input type="number" placeholder="max" value={dGnarrMax} onChange={e => setDGnarrMax(e.target.value)} style={numStyle} data-id="element-944" data-mp="fv9phbuyar82" />
      </div>
      {dCloseQs.length > 0 && renderDropdown(dCloseQs, dCloseQFilter, setDCloseQFilter, dCloseQDropdownOpen, setDCloseQDropdownOpen)}
      {dOfferings.length > 0 && renderDropdown(dOfferings, dOfferingFilter, setDOfferingFilter, dOfferingDropdownOpen, setDOfferingDropdownOpen)}
      {dSubs.length > 0 && renderDropdown(dSubs, dSubFilter, setDSubFilter, dSubDropdownOpen, setDSubDropdownOpen)}
    </div>;
  return <>
      {filterBar}
      <div style={{
      fontSize: 9,
      color: '#8892a0',
      marginBottom: 4
    }} data-id="element-945" data-mp="1lo5xbqtb7prq">{filtered.length} of {requests.length} requests</div>
      <DealExpandedViewInner requests={sorted} mode={mode} data-id="element-946" data-mp="1hmazwxei1wj8" />
    </>;
}
function DealExpandedViewInner({
  requests,
  mode
}: {
  requests: Request[];
  mode: RequestViewMode;
}) {
  switch (mode) {
    case 'table':
      return <TableView requests={requests} data-id="element-947" data-mp="17pog8bxwtbis" />;
    case 'tile':
      return <TileView requests={requests} data-id="element-948" data-mp="3718rrsaie38p" />;
    case 'gallery':
      return <GalleryView requests={requests} data-id="element-949" data-mp="36zei5a4otxjx" />;
    case 'waterfall':
      return <WaterfallView requests={requests} data-id="element-950" data-mp="323fob70lbnc4" />;
    case 'cards':
    default:
      return <div className="deal-grid" data-id="element-951" data-mp="10cyram8a3nne">
          {requests.map(r => <div key={`${r.req_id}|${r.user_sc_name}`} className="deal-chip" data-id="element-952" data-mp="153ot5phhx8dc">
              <div className="dc-id" data-id="element-953" data-mp="155rtb2cd4hvs">{r.opp_id}</div>
              <div className="dc-name" data-id="element-954" data-mp="qg3pnugpl74w">{r.acc_name.substring(0, 35)}</div>
              <div style={{
            fontSize: 9,
            marginTop: 3
          }} data-id="element-955" data-mp="vdvrphb2i9mt"><RequestLink r={r} data-id="element-956" data-mp="vendy1iryv0f" /></div>
              <div style={{
            fontSize: 8,
            color: '#667085',
            marginTop: 2
          }} data-id="element-957" data-mp="1j3q4jyvdzy72">{r.req_entry}</div>
              <div className="dc-tags" data-id="element-958" data-mp="1j1vg4akwpny8">
            <span className={`badge ${ageSeverity(r.req_age) === 'high' ? 'b-red' : ageSeverity(r.req_age) === 'medium' ? 'b-amber' : 'b-gray'}`} data-id="element-959" data-mp="1o32syo1wpdbr">{formatReqAge(r.req_age)}</span>
                <span className="badge b-blue" data-id="element-960" data-mp="1e43svtv7iq0d">{fmtG(r.opp_gnarr)}</span>
                <span className="badge b-purple" style={{
              fontSize: 8
            }} data-id="element-961" data-mp="1e6g9o3p8aubu">{r.opp_close_q}</span>
                {isReqAged(r) && <span className="badge b-red" data-id="element-962" data-mp="2pghr96smhic">⚠ aged</span>}
              </div>
              <div style={{
            fontSize: 9,
            color: '#8892a0',
            marginTop: 3
          }} data-id="element-963" data-mp="2ndhlwbxf7zw">
                {r.req_sub_region} · {r.req_offering?.substring(0, 26)}
              </div>
            </div>)}
        </div>;
  }
}

/* ─── SC Matrix (unchanged) ───────────────────────────────────────────── */

function SCMatrix({
  region,
  sub,
  cqState,
  data
}: {
  region: string;
  sub: string[];
  cqState: TabState;
  data: Request[];
}) {
  const ipData = data.filter(r => r.req_status === 'In Progress' && (region === 'Global' || r.opp_region === region) && matchesSub(sub, r.req_sub_region) && matchesCQFilter(r, cqState));
  const COLS = [...PRODUCT_BUCKETS.map(b => b.label), 'Other'];
  const scMap: Record<string, Record<string, number>> = {};
  const seenDR: Record<string, number> = {};
  for (const r of ipData) {
    if (!r.user_sc_name || !r.user_sc_name.trim()) continue; // blank SC is not counted
    const bkts = bucketProducts(r.req_products || '');
    for (const scName of splitUserSCNames(r.user_sc_name)) {
      if (!scMap[scName]) scMap[scName] = {};
      for (const bkt of bkts) {
        const uniq = scName + '|' + bkt + '|' + r.opp_id;
        if (seenDR[uniq]) continue;
        seenDR[uniq] = 1;
        scMap[scName][bkt] = (scMap[scName][bkt] || 0) + 1;
      }
    }
  }
  const scRows = Object.entries(scMap).map(([name, bkts]) => ({
    name: name.split(' ')[0],
    full: name,
    bkts,
    total: Object.values(bkts).reduce((a, b) => a + b, 0)
  })).sort((a, b) => b.total - a.total);
  const colTotals: Record<string, number> = {};
  for (const c of COLS) colTotals[c] = 0;
  for (const row of scRows) {
    for (const c of COLS) colTotals[c] += row.bkts[c] || 0;
  }
  const grandTotal = Object.values(colTotals).reduce((a, b) => a + b, 0);
  const activeCols = COLS.filter(c => colTotals[c] > 0);
  if (scRows.length === 0 || grandTotal === 0) {
    return <tr data-id="element-964" data-mp="ljdih4jlj21y">
        <td colSpan={2} style={{
        padding: '12px 10px',
        color: '#8892a0',
        fontSize: 10,
        fontStyle: 'italic'
      }} data-id="element-965" data-mp="qfvmg273qwd8">
          No in-progress deals match the current filter.
        </td>
      </tr>;
  }
  return <>
      <thead data-id="element-966" data-mp="e5mfr2lkedl5">
        <tr data-id="element-967" data-mp="e54c3kwkox37">
          <th data-id="element-968" data-mp="e4bfvdv8ppzb">SC</th>
          {activeCols.map(c => <th key={c} data-id="element-969" data-mp="96t5dj1kr2p7">{c}</th>)}
          <th data-id="element-970" data-mp="97uq6f8e73k4">Total</th>
        </tr>
      </thead>
      <tbody data-id="element-971" data-mp="94gnf5vtgsc5">
        {scRows.map(row => <tr key={row.full} data-id="element-972" data-mp="48hvj8ba4s6z">
            <td title={row.full} data-id="element-973" data-mp="49jgq5m0ev2f">{row.name}</td>
            {activeCols.map(c => <td key={c} data-id="element-974" data-mp="11u5rxt7qjaii">
                {(row.bkts[c] || 0) > 0 ? <span className="mx-val" data-id="element-975" data-mp="wvud142l29tf">{row.bkts[c]}</span> : <span style={{
            color: '#d1d5db'
          }} data-id="element-976" data-mp="wujdjgg67o84">—</span>}
              </td>)}
            <td data-id="element-977" data-mp="rzdpaazapxgv"><span className="mx-val" style={{
            background: '#e0e7ff',
            color: '#3730a3'
          }} data-id="element-978" data-mp="rz402pm2ruc8">{row.total}</span></td>
          </tr>)}
      </tbody>
      <tfoot data-id="element-979" data-mp="3col03573i0r3">
        <tr data-id="element-980" data-mp="3cobcfo9iw5ok">
          <td data-id="element-981" data-mp="3cote50eyfc3v">Total</td>
          {activeCols.map(c => <td key={c} data-id="element-982" data-mp="37o5oai2gp1b8"><span style={{
            fontWeight: 700,
            color: '#1d4ed8'
          }} data-id="element-983" data-mp="37ndrj3x4oxjd">{colTotals[c] || 0}</span></td>)}
          <td data-id="element-984" data-mp="1zqb2uapf7u6y"><span style={{
            fontWeight: 700,
            color: '#3730a3'
          }} data-id="element-985" data-mp="1zqb1a69rvm4u">{grandTotal}</span></td>
        </tr>
      </tfoot>
    </>;
}

/* ─── Touchpoint expanded view ──────────────────────────────────────────── */

function TouchpointExpandedView({
  touchpoints,
  mode
}: {
  touchpoints: Touchpoint[];
  mode: RequestViewMode;
}) {
  const [tSearchId, setTSearchId] = useState('');
  const [tSearchName, setTSearchName] = useState('');
  const [tSearchAccount, setTSearchAccount] = useState('');
  const [tSearchSc, setTSearchSc] = useState('');
  const [tSearchComments, setTSearchComments] = useState('');
  const [tTypeFilter, setTypeFilter] = useState<string[]>([]);
  const [tTypeDropdownOpen, setTypeDropdownOpen] = useState(false);
  const [tEntryFrom, setTEntryFrom] = useState('');
  const [tEntryTo, setTEntryTo] = useState('');
  const [tCloseQFilter, setTCloseQFilter] = useState<string[]>([]);
  const [tCloseQDropdownOpen, setTCloseQDropdownOpen] = useState(false);
  const [tSubFilter, setTSubFilter] = useState<string[]>([]);
  const [tSubDropdownOpen, setTSubDropdownOpen] = useState(false);
  const tTypes = [...new Set(touchpoints.map(t => t.tp_type).filter((v): v is string => Boolean(v)))].sort();
  const tCloseQs = [...new Set(touchpoints.map(t => t.opp_close_q).filter((v): v is string => Boolean(v)))].sort();
  const tSubs = [...new Set(touchpoints.map(t => t.opp_market_area).filter((v): v is string => Boolean(v)))].sort();
  const parseEntry = (entry: string): Date | null => {
    if (!entry) return null;
    const p = entry.split('/');
    if (p.length !== 3) return null;
    const [dd, mm, yy] = p.map(Number);
    return new Date(yy > 50 ? 1900 + yy : 2000 + yy, mm - 1, dd);
  };
  const filtered = touchpoints.filter(t => {
    if (tSearchId && !t.opp_id.toLowerCase().includes(tSearchId.toLowerCase())) return false;
    if (tSearchName && !t.tp_name.toLowerCase().includes(tSearchName.toLowerCase())) return false;
    if (tSearchAccount && !t.acc_name.toLowerCase().includes(tSearchAccount.toLowerCase())) return false;
    if (tSearchSc && !t.user_sc_name.toLowerCase().includes(tSearchSc.toLowerCase())) return false;
    if (tSearchComments && !(t.tp_solution_comments || '').toLowerCase().includes(tSearchComments.toLowerCase())) return false;
    if (tTypeFilter.length > 0 && !tTypeFilter.includes(t.tp_type)) return false;
    if (tEntryFrom || tEntryTo) {
      const dt = parseEntry(t.tp_date);
      if (dt) {
        if (tEntryFrom) {
          const f = new Date(tEntryFrom);
          if (dt < f) return false;
        }
        if (tEntryTo) {
          const te = new Date(tEntryTo);
          if (dt > te) return false;
        }
      }
    }
    if (tCloseQFilter.length > 0 && !tCloseQFilter.includes(t.opp_close_q)) return false;
    if (tSubFilter.length > 0 && !tSubFilter.includes(t.opp_market_area)) return false;
    return true;
  });
  if (touchpoints.length === 0) {
    return <div style={{
      fontSize: 10,
      color: '#8892a0',
      padding: '12px 4px',
      textAlign: 'center'
    }} data-id="element-986" data-mp="30dk90yu4kmud">No touchpoints for this SC</div>;
  }
  const inputStyle = {
    fontSize: 10,
    padding: '4px 8px',
    border: '1px solid #d0d5dd',
    borderRadius: 5,
    outline: 'none',
    width: 110,
    color: '#344054',
    background: '#fff'
  };
  const dateStyle = {
    fontSize: 10,
    padding: '3px 6px',
    border: '1px solid #d0d5dd',
    borderRadius: 5,
    outline: 'none',
    color: '#344054',
    background: '#fff'
  };
  const btnStyle = {
    fontSize: 10,
    padding: '4px 8px',
    border: '1px solid #d0d5dd',
    borderRadius: 5,
    outline: 'none',
    color: '#344054',
    background: '#fff',
    cursor: 'pointer'
  };
  const ddStyle = {
    position: 'absolute' as const,
    top: '100%',
    left: 0,
    marginTop: 4,
    zIndex: 50,
    background: '#fff',
    border: '1px solid #d0d5dd',
    borderRadius: 5,
    boxShadow: '0 2px 6px rgba(0,0,0,.12)',
    minWidth: 140,
    padding: '4px 0'
  };
  const renderDropdown = (label: string, items: string[], selected: string[], toggle: (v: string[]) => void, open: boolean, setOpen: (v: boolean) => void) => <div style={{
    position: 'relative'
  }} data-id="element-987" data-mp="awvm7rwwzihj">
      <button style={btnStyle} onClick={() => setOpen(!open)} data-id="element-988" data-mp="3gvd98lsng3lh">
        {selected.length > 0 ? `${label} (${selected.length})` : `${label} ▾`}
      </button>
      {open && <div style={ddStyle} data-id="element-989" data-mp="3pwkvu6lszfzq">
          <div onClick={() => toggle(selected.length === items.length ? [] : [...items])} style={{
        padding: '4px 10px',
        fontSize: 10,
        cursor: 'pointer',
        color: '#667085',
        borderBottom: '1px solid #eaecf0'
      }} data-id="element-990" data-mp="3pwtcm9l1r5g7">
            {selected.length === items.length ? 'Deselect all' : 'Select all'}
          </div>
          {items.map(item => <label key={item} style={{
        padding: '4px 10px',
        fontSize: 10,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: 6
      }} onClick={e => e.stopPropagation()} data-id="element-991" data-mp="2t47njq012h39">
              <input type="checkbox" checked={selected.includes(item)} onChange={() => toggle(selected.includes(item) ? selected.filter(x => x !== item) : [...selected, item])} data-id="element-992" data-mp="324w00g9rw9e1" />
              {item}
            </label>)}
        </div>}
    </div>;
  const toComparable = (entry: string) => {
    if (!entry) return 0;
    const [dd, mm, yy] = entry.split('/');
    return +yy * 10000 + +mm * 100 + +dd;
  };
  const sorted = [...filtered].sort((a, b) => toComparable(b.tp_date) - toComparable(a.tp_date));
  const filterBar = <div style={{
    padding: '6px 0',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    flexWrap: 'wrap'
  }} data-id="element-993" data-mp="kzguoqapbfpt">
      <input type="text" placeholder="ID" value={tSearchId} onChange={e => setTSearchId(e.target.value)} style={inputStyle} data-id="element-994" data-mp="18pkwcs4bx5e6" />
      <input type="text" placeholder="Name" value={tSearchName} onChange={e => setTSearchName(e.target.value)} style={inputStyle} data-id="element-995" data-mp="1cnqz7p21r0tt" />
      <input type="text" placeholder="Account" value={tSearchAccount} onChange={e => setTSearchAccount(e.target.value)} style={inputStyle} data-id="element-996" data-mp="13owx5j5y5i8v" />
      <input type="text" placeholder="SC" value={tSearchSc} onChange={e => setTSearchSc(e.target.value)} style={inputStyle} data-id="element-997" data-mp="3jfjc0q5j1o0g" />
      <input type="text" placeholder="Comments" value={tSearchComments} onChange={e => setTSearchComments(e.target.value)} style={{
      ...inputStyle,
      width: 120
    }} data-id="element-998" data-mp="3odb71t1gxq8v" />
      {renderDropdown('Type', tTypes, tTypeFilter, setTypeFilter, tTypeDropdownOpen, setTypeDropdownOpen)}
      <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 3,
      fontSize: 10,
      color: '#667085'
    }} data-id="element-999" data-mp="2vjofhwlm4hsr">
        <span data-id="element-1000" data-mp="2qld7zsisijdp">Entry</span>
        <input type="date" value={tEntryFrom} onChange={e => setTEntryFrom(e.target.value)} style={dateStyle} data-id="element-1001" data-mp="2qjs9jrtcugak" />
        <span data-id="element-1002" data-mp="2gnp000w6bfdl">–</span>
        <input type="date" value={tEntryTo} onChange={e => setTEntryTo(e.target.value)} style={dateStyle} data-id="element-1003" data-mp="2gn75b8p5t97s" />
      </div>
      {renderDropdown('Close Q', tCloseQs, tCloseQFilter, setTCloseQFilter, tCloseQDropdownOpen, setTCloseQDropdownOpen)}
      {renderDropdown('Sub', tSubs, tSubFilter, setDSubFilter, tSubDropdownOpen, setTSubDropdownOpen)}
    </div>;
  return <>
      {filterBar}
      <div style={{
      fontSize: 9,
      color: '#8892a0',
      marginBottom: 4
    }} data-id="element-1004" data-mp="5v4blzwcmb7i">{sorted.length} of {touchpoints.length} touchpoints</div>
      <TouchpointExpandedViewInner touchpoints={sorted} mode={mode} data-id="element-1005" data-mp="dedkls0y07w1" />
    </>;
}
function TouchpointExpandedViewInner({
  touchpoints,
  mode
}: {
  touchpoints: Touchpoint[];
  mode: RequestViewMode;
}) {
  switch (mode) {
    case 'table':
      return <TouchpointTableView touchpoints={touchpoints} data-id="element-1006" data-mp="n7carec386hr" />;
    case 'tile':
      return <TouchpointTileView touchpoints={touchpoints} data-id="element-1007" data-mp="s39odcvyuyqu" />;
    case 'gallery':
      return <TouchpointGalleryView touchpoints={touchpoints} data-id="element-1008" data-mp="3qn052six4no4" />;
    case 'waterfall':
      return <TouchpointWaterfallView touchpoints={touchpoints} data-id="element-1009" data-mp="3vk026dxsig33" />;
    case 'cards':
    default:
      return <div className="deal-grid" data-id="element-1010" data-mp="4dfcoyndvg86">
          {touchpoints.map(t => <div key={t.tp_id} className="deal-chip" title={`${t.acc_name}\n${t.tp_name}\nType: ${t.tp_type || '—'}\nDate: ${t.tp_date}`} data-id="element-1011" data-mp="8fttzebem7xc">
              <div style={{
            fontSize: 9,
            color: '#8892a0'
          }} data-id="element-1012" data-mp="1fsvqo97mxu32">{t.opp_id}</div>
              <div style={{
            fontSize: 10,
            fontWeight: 600,
            marginTop: 2
          }} data-id="element-1013" data-mp="1juh887v46wjl">{t.acc_name.substring(0, 16)}</div>
              <div style={{
            fontSize: 9,
            marginTop: 2
          }} data-id="element-1014" data-mp="1if4udsn7es1j">
                <span title={t.tp_name} data-id="element-1015" data-mp="1df0bxx2qjn8o">{t.tp_name.substring(0, 20)}{t.tp_name.length > 20 ? '…' : ''}</span>
              </div>
              <div style={{
            display: 'flex',
            gap: 4,
            marginTop: 4,
            flexWrap: 'wrap'
          }} data-id="element-1016" data-mp="1scj5rdu9sqmz">
                {t.tp_type && <span className="badge b-blue" style={{
              fontSize: 8
            }} data-id="element-1017" data-mp="1nc4y3wwkziph">{t.tp_type}</span>}
                <span style={{
              fontSize: 8,
              color: '#667085'
            }} data-id="element-1018" data-mp="21c5yzccuiq4y">{t.tp_date}</span>
              </div>
              <div style={{
            fontSize: 8,
            color: '#667085',
            marginTop: 2
          }} data-id="element-1019" data-mp="1wemjk94ew7bf">{t.opp_market_area} · {t.opp_close_q}</div>
            </div>)}
        </div>;
  }
}

/* ─── Touchpoint view renderers ──────────────────────────────────────────── */

function TouchpointTableView({
  touchpoints
}: {
  touchpoints: Touchpoint[];
}) {
  const tpColumns: SortableColumn<Touchpoint>[] = [{
    key: 'opp_id',
    label: 'ID',
    width: '10%',
    sortValue: t => t.opp_id,
    render: t => <span style={{
      color: '#8892a0',
      fontSize: 9
    }} data-id="element-1020" data-mp="2yq4fs9n40wdh">{t.opp_id}</span>
  }, {
    key: 'tp_name',
    label: 'Name',
    width: '16%',
    sortValue: t => t.tp_name,
    render: t => <span title={t.tp_name} data-id="element-1021" data-mp="33ofr6ot1zext">{t.tp_name.substring(0, 22)}</span>
  }, {
    key: 'acc_name',
    label: 'Account',
    width: '14%',
    sortValue: t => t.acc_name,
    render: t => <span title={t.acc_name} data-id="element-1022" data-mp="2unhm036vp1dn">{t.acc_name.substring(0, 18)}</span>
  }, {
    key: 'user_sc_name',
    label: 'SC',
    width: '10%',
    sortValue: t => t.user_sc_name,
    render: t => <ScNamesCell names={t.user_sc_name} data-id="element-1023" data-mp="2kq35k3kygclc" />
  }, {
    key: 'tp_type',
    label: 'Type',
    width: '12%',
    sortValue: t => t.tp_type || '',
    render: t => t.tp_type ? <span className="badge b-blue" style={{
      fontSize: 8
    }} data-id="element-1024" data-mp="270j9thpskflj">{t.tp_type}</span> : <span data-id="element-1025" data-mp="2b1lam2zcwzpm">—</span>
  }, {
    key: 'tp_date',
    label: 'Date',
    width: '10%',
    sortValue: t => ddmmyyToSortValue(t.tp_date),
    render: t => <span style={{
      fontSize: 9,
      color: '#667085'
    }} data-id="element-1026" data-mp="1x1k70frpje6g">{t.tp_date}</span>
  }, {
    key: 'tp_solution_comments',
    label: 'Comments',
    width: '18%',
    sortValue: t => t.tp_solution_comments || '',
    render: t => <span title={t.tp_solution_comments} data-id="element-1027" data-mp="7lrfoz00rcxt">{t.tp_solution_comments ? t.tp_solution_comments.substring(0, 30) + (t.tp_solution_comments.length > 30 ? '…' : '') : '—'}</span>
  }];
  return <SortableTable columns={tpColumns} data={touchpoints} rowKey={t => t.tp_id} data-id="element-1028" data-mp="3ux8uyngwgjbd" />;
}
function TouchpointTileView({
  touchpoints
}: {
  touchpoints: Touchpoint[];
}) {
  return <div className="deal-tile-grid" data-id="element-1029" data-mp="qhhj8qnlohdt">
      {touchpoints.map(t => <div key={t.tp_id} className="deal-tile" title={`${t.acc_name}\n${t.tp_name}\nType: ${t.tp_type || '—'}\nDate: ${t.tp_date}`} data-id="element-1030" data-mp="mh71kgqg42j4">
          <div className="dt-id" data-id="element-1031" data-mp="hgrnfmycar03">{t.opp_id}</div>
          <div className="dt-acct" data-id="element-1032" data-mp="hjnlrce3nhp2">{t.acc_name.substring(0, 14)}</div>
          <div style={{
        fontSize: 8,
        marginTop: 2,
        lineHeight: 1.2
      }} data-id="element-1033" data-mp="ci86k0yls5el">
            <span title={t.tp_name} data-id="element-1034" data-mp="3293a80puiubj">{t.tp_name.substring(0, 16)}</span>
          </div>
          <div style={{
        fontSize: 8,
        color: '#667085',
        marginTop: 2
      }} data-id="element-1035" data-mp="2x8fj7f1madhb">{t.tp_date}</div>
          <span className="badge b-blue" style={{
        fontSize: 8
      }} data-id="element-1036" data-mp="2n5a7imlhw655">{t.tp_type || '—'}</span>
        </div>)}
    </div>;
}
function TouchpointGalleryView({
  touchpoints
}: {
  touchpoints: Touchpoint[];
}) {
  return <div className="deal-gallery" data-id="element-1037" data-mp="2x2y8vvdlwbwa">
      {touchpoints.map(t => <div key={t.tp_id} className="deal-gallery-card" data-id="element-1038" data-mp="31ty5ezitcry9">
          <div className="dg-id" data-id="element-1039" data-mp="31vhmgscan713">{t.opp_id}</div>
          <div className="dg-title" data-id="element-1040" data-mp="35vrxijey0nmt">{t.acc_name}</div>
          <div style={{
        fontSize: 10,
        marginBottom: 4
      }} data-id="element-1041" data-mp="35v9vt79ihh7i">
            <span title={t.tp_name} data-id="element-1042" data-mp="3attk3ebkv531">{t.tp_name.substring(0, 30)}{t.tp_name.length > 30 ? '…' : ''}</span>
          </div>
          <div style={{
        fontSize: 9,
        color: '#667085',
        marginBottom: 4
      }} data-id="element-1043" data-mp="3fv0pj6q8zs6j">{t.tp_entry}</div>
          <div style={{
        fontSize: 9,
        color: '#344054',
        marginBottom: 4,
        lineHeight: 1.3
      }} data-id="element-1044" data-mp="3krr6dw9vmrt0">
            <span title={t.tp_solution_comments} data-id="element-1045" data-mp="1lgq292pax62v">{t.tp_solution_comments ? t.tp_solution_comments.substring(0, 50) + (t.tp_solution_comments.length > 50 ? '…' : '') : '—'}</span>
          </div>
          <div className="dg-meta" data-id="element-1046" data-mp="3sbjl86qujmf7">
            {t.tp_type && <span className="badge b-blue" data-id="element-1047" data-mp="3sa8hu81bcgom">{t.tp_type}</span>}
            <span className="badge b-gray" data-id="element-1048" data-mp="3ncfedmrea0s1">{t.opp_close_q}</span>
          </div>
          <div className="dg-footer" data-id="element-1049" data-mp="5x08sflfmhbd">
            {t.opp_market_area} · {t.user_sc_name}
          </div>
        </div>)}
    </div>;
}
const TP_TYPE_BUCKETS = ['Internal Alignment', 'Discovery', 'Live Demo', 'Customer Meeting'];
function TouchpointWaterfallView({
  touchpoints
}: {
  touchpoints: Touchpoint[];
}) {
  const groups: Record<string, Touchpoint[]> = {};
  const BUCKETS_WITH_OTHERS = [...TP_TYPE_BUCKETS, 'Others'];

  // Initialize all buckets
  for (const b of BUCKETS_WITH_OTHERS) groups[b] = [];

  // Distribute touchpoints into buckets
  for (const t of touchpoints) {
    const tpType = t.tp_type || '';
    if (TP_TYPE_BUCKETS.includes(tpType)) {
      groups[tpType].push(t);
    } else {
      groups['Others'].push(t);
    }
  }
  return <div className="deal-waterfall" data-id="element-1050" data-mp="115u1isu07fa8">
      {BUCKETS_WITH_OTHERS.map(bucket => {
      const items = groups[bucket] || [];
      return <div key={bucket} className="deal-waterfall-lane" data-id="element-1051" data-mp="165yngydol27u">
            <div className="deal-waterfall-lane-header" data-id="element-1052" data-mp="r7weg8nvroj7">
              <span data-id="element-1053" data-mp="r9h9s0i0rbi4">{bucket}</span>
              <span className="lane-count" data-id="element-1054" data-mp="w6ra4b6y57kq">{items.length}</span>
            </div>
            <div className="deal-waterfall-lane-body" data-id="element-1055" data-mp="w8tykqsgasnc">
              {items.length === 0 && <div style={{
            fontSize: 9,
            color: '#d1d5db',
            textAlign: 'center',
            padding: '12px 4px',
            fontStyle: 'italic'
          }} data-id="element-1056" data-mp="1jyeqhz6l108r">none</div>}
              {items.map(t => <div key={t.tp_id} className="deal-waterfall-card" data-id="element-1057" data-mp="1a68py129lahy">
                  <div className="dw-id" data-id="element-1058" data-mp="1f4jzsbsk7l06">{t.opp_id}</div>
                  <div className="dw-acct" title={t.acc_name} data-id="element-1059" data-mp="22v68846h77ap">{t.acc_name.substring(0, 18)}</div>
                  <div style={{
              fontSize: 8,
              marginTop: 2,
              lineHeight: 1.2
            }} data-id="element-1060" data-mp="22td1ypzryp38">
                    <span title={t.tp_name} data-id="element-1061" data-mp="27nzckqy18dm9">{t.tp_name.substring(0, 20)}</span>
                  </div>
                  <div style={{
              fontSize: 8,
              color: '#667085',
              marginTop: 2
            }} data-id="element-1062" data-mp="37ch7qc1k98dn">{t.tp_entry}</div>
                  <div className="dw-tags" data-id="element-1063" data-mp="3b9ve7vxux21y">
                    <span className="badge b-blue" style={{
                fontSize: 8
              }} data-id="element-1064" data-mp="2xcqai2lm8f9v">{t.tp_type || '—'}</span>
                  </div>
                </div>)}
            </div>
          </div>;
    })}
    </div>;
}

/* ─── Main panel ───────────────────────────────────────────────────────── */

export function ODPanel({
  state
}: Props) {
  const [expandedSC, setExpandedSC] = useState<string | null>(null);
  const [dealView, setDealView] = useState<RequestViewMode>('cards');
  const [expandedViewMode, setExpandedViewMode] = useState<'deals' | 'touchpoints'>('deals');
  const [scSearch, setScSearch] = useState('');
  const [scSelected, setScSelected] = useState<string[]>([]);
  const [scDropdownOpen, setScDropdownOpen] = useState(false);
  const scDropdownRef = useRef<HTMLDivElement>(null);
  const scDropdownBtnRef = useRef<HTMLButtonElement>(null);
  const [scDropdownPos, setScDropdownPos] = useState<{
    top: number;
    left: number;
  }>({
    top: 0,
    left: 0
  });
  const [scSortedRows, setScSortedRows] = useState<SCData[]>([]);
  const [longLeaveNames, setLongLeaveNames] = useState<Set<string>>(new Set());
  const [hiddenScNames, setHiddenScNames] = useState<Set<string>>(new Set());
  const [hideInactive, setHideInactive] = useState(true);
  const [productSelected, setProductSelected] = useState<string[]>([]);
  const [productSearch, setProductSearch] = useState('');
  const [productDropdownOpen, setProductDropdownOpen] = useState(false);
  const productDropdownRef = useRef<HTMLDivElement>(null);
  const productDropdownBtnRef = useRef<HTMLButtonElement>(null);
  const [productDropdownPos, setProductDropdownPos] = useState<{
    top: number;
    left: number;
  }>({
    top: 0,
    left: 0
  });
  const {
    requests,
    touchpoints
  } = useRequests();
  const closedMode = getQuarterMode(state) === 'closed';
  useEffect(() => {
    // Contributor status is per-year: with several FYs selected use the latest one.
    const selectedFy = state.fy.filter(y => y !== 'All').sort();
    const year = selectedFy.length > 0 ? selectedFy[selectedFy.length - 1] : String(new Date().getFullYear());
    let cancelled = false;
    fetchContributorStatus(year).then(result => {
      if (cancelled || !result) return;
      const leave = new Set<string>();
      const hidden = new Set<string>();
      result.records.forEach(rec => {
        const name = rec.contributorName.toLowerCase();
        if (rec.isLongLeave) leave.add(name);
        if (rec.display === false) hidden.add(name);
      });
      setLongLeaveNames(leave);
      setHiddenScNames(hidden);
    });
    return () => {
      cancelled = true;
    };
  }, [state.fy]);
  useEffect(() => {
    if (!scDropdownOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (scDropdownRef.current && !scDropdownRef.current.contains(e.target as Node)) {
        setScDropdownOpen(false);
      }
    };
    // The panel is position:fixed (to escape the table container's overflow:hidden),
    // so close it when the page scrolls or resizes instead of leaving it detached.
    const handleScroll = (e: Event) => {
      if (scDropdownRef.current && e.target instanceof Node && scDropdownRef.current.contains(e.target)) return;
      setScDropdownOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleScroll);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleScroll);
    };
  }, [scDropdownOpen]);
  useEffect(() => {
    if (!productDropdownOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (productDropdownRef.current && !productDropdownRef.current.contains(e.target as Node)) {
        setProductDropdownOpen(false);
      }
    };
    // Same as the SC dropdown: the panel is position:fixed, so close it on page scroll/resize.
    const handleScroll = (e: Event) => {
      if (productDropdownRef.current && e.target instanceof Node && productDropdownRef.current.contains(e.target)) return;
      setProductDropdownOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleScroll);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleScroll);
      // The in-dropdown search text resets whenever the dropdown closes.
      setProductSearch('');
    };
  }, [productDropdownOpen]);

  // Touchpoints filters
  const [tpSearchId, setTpSearchId] = useState('');
  const [tpSearchName, setTpSearchName] = useState('');
  const [tpSearchAccount, setTpSearchAccount] = useState('');
  const [tpSearchSc, setTpSearchSc] = useState('');
  const [tpTypeFilter, setTpTypeFilter] = useState<string[]>([]);
  const [tpTypeDropdownOpen, setTpTypeDropdownOpen] = useState(false);
  const [tpEntryFrom, setTpEntryFrom] = useState('');
  const [tpEntryTo, setTpEntryTo] = useState('');
  const tpTypes = [...new Set(touchpoints.map(t => t.tp_type).filter(Boolean))].sort();
  const parseEntryDate = (entry: string): Date | null => {
    if (!entry) return null;
    const parts = entry.split('/');
    if (parts.length !== 3) return null;
    const [dd, mm, yy] = parts.map(Number);
    const year = yy > 50 ? 1900 + yy : 2000 + yy;
    return new Date(year, mm - 1, dd);
  };
  const touchpointsFiltered = touchpoints.filter(t => {
    if (state.r !== 'Global' && t.opp_region !== state.r) return false;
    if (!matchesSub(state.sub, t.opp_sub_region)) return false;
    if (!matchesTouchpointFilter(t, state)) return false;
    if (tpSearchId && !t.opp_id.toLowerCase().includes(tpSearchId.toLowerCase())) return false;
    if (tpSearchName && !t.tp_name.toLowerCase().includes(tpSearchName.toLowerCase())) return false;
    if (tpSearchAccount && !t.acc_name.toLowerCase().includes(tpSearchAccount.toLowerCase())) return false;
    if (tpSearchSc && !t.user_sc_name.toLowerCase().includes(tpSearchSc.toLowerCase())) return false;
    if (tpTypeFilter.length > 0 && !tpTypeFilter.includes(t.tp_type)) return false;
    if (tpEntryFrom || tpEntryTo) {
      const d = parseEntryDate(t.tp_entry);
      if (d) {
        if (tpEntryFrom) {
          const from = parseEntryDate(tpEntryFrom);
          if (from && d < from) return false;
        }
        if (tpEntryTo) {
          const to = parseEntryDate(tpEntryTo);
          if (to && d > to) return false;
        }
      }
    }
    return true;
  });

  // Product options come from every request in the report (not narrowed by region/FY/quarter).
  // Product names are compared case-insensitively, so case variants (e.g. "Marketo" / "MARKETO")
  // collapse into a single option (the first spelling seen).
  const productOptions = useMemo(() => {
    const byKey = new Map<string, string>();
    for (const r of requests) {
      for (const p of splitProducts(r.req_products)) {
        const key = p.toLowerCase();
        if (!byKey.has(key)) byKey.set(key, p);
      }
    }
    return [...byKey.values()].sort((a, b) => a.localeCompare(b));
  }, [requests]);
  const productSelectedLower = new Set(productSelected.map(p => p.toLowerCase()));
  const matchesSelectedProduct = (products: string | null | undefined) => splitProducts(products).some(p => productSelectedLower.has(p.toLowerCase()));
  const productRequests = productSelected.length === 0 ? requests : requests.filter(r => matchesSelectedProduct(r.req_products));
  // SC rows/numbers are built from product-matching requests only; the SC dropdown options
  // are built from all requests so they don't change when products are selected.
  const scDataBase = buildSCList(state.r, state.sub, state, requests);
  const scDataAll = productSelected.length === 0 ? scDataBase : buildSCList(state.r, state.sub, state, productRequests);
  // Touchpoints carry their own comma-separated products (e.g. "ACS SUBSCRIPTIONS,MARKETO").
  const productTouchpoints = productSelected.length === 0 ? touchpoints : touchpoints.filter(t => matchesSelectedProduct(t.tp_products));
  const tpSCDataAll = buildTouchpointSCList(state.r, state.sub, state, productTouchpoints);
  const tpSCMap = new Map(tpSCDataAll.map(tp => [tp.n, tp]));
  if (touchpoints.length > 0 && tpSCDataAll.length <= 2) {
    const scSamples = touchpoints.slice(0, 5).map(t => t.user_sc_name);
    console.log('[ODPanel] touchpoint sc samples:', scSamples);
    console.log('[ODPanel] unique sc values:', new Set(touchpoints.map(t => t.user_sc_name)).size);
  }
  const applyHideInactive = (list: SCData[]) => hideInactive ? list.filter(s => !hiddenScNames.has(s.n.toLowerCase())) : list;
  const scVisible = applyHideInactive(scDataAll);
  const scOptions = [...new Set(applyHideInactive(scDataBase).map(s => s.n))].sort((a, b) => a.localeCompare(b));
  const visibleProducts = productOptions.filter(p => !productSearch || p.toLowerCase().includes(productSearch.toLowerCase()));
  const allVisibleProductsSelected = visibleProducts.length > 0 && visibleProducts.every(p => productSelected.includes(p));
  // Selections no longer in the list (e.g. after a region/sub/quarter change) are ignored.
  const effectiveScSelected = scSelected.filter(n => scOptions.includes(n));
  const scFiltered = scVisible.filter(s => !scSearch || s.n.toLowerCase().includes(scSearch.toLowerCase())).filter(s => effectiveScSelected.length === 0 || effectiveScSelected.includes(s.n));

  // On-hold requests per SC, filtered like the SC rows (region / sub / quarter / product).
  const onHoldBySC = useMemo(() => {
    const map = new Map<string, Request[]>();
    for (const r of productRequests) {
      if (r.req_status !== 'On Hold') continue;
      if (state.r !== 'Global' && r.opp_region !== state.r) continue;
      if (!matchesSub(state.sub, r.req_sub_region)) continue;
      if (!matchesCQFilter(r, state)) continue;
      for (const name of splitUserSCNames(r.user_sc_name)) {
        const list = map.get(name) ?? [];
        list.push(r);
        map.set(name, list);
      }
    }
    return map;
  }, [productRequests, state]);
  const allIP = requests.filter(r => {
    if (r.req_status !== 'In Progress') return false;
    if (state.r !== 'Global' && r.opp_region !== state.r) return false;
    if (!matchesSub(state.sub, r.req_sub_region)) return false;
    if (!matchesCQFilter(r, state)) return false;
    return true;
  });
  const scs = new Set(allIP.flatMap(r => splitUserSCNames(r.user_sc_name))).size;

  // oppGnarr includes ALL statuses (not just IP), matching region/sub/CQ
  const allForGnarr = requests.filter(r => {
    if (state.r !== 'Global' && r.opp_region !== state.r) return false;
    if (!matchesSub(state.sub, r.req_sub_region)) return false;
    if (!matchesCQFilter(r, state)) return false;
    return true;
  });
  const oppGnarr = sumUniqueByOpp(allForGnarr, d => d.opp_gnarr);
  const oppGnarrTotal = sumUniqueByOpp(allForGnarr, r => r.opp_gnarr_grand_total);
  const ages = validReqAges(allForGnarr);
  const maxIP = Math.max(...scFiltered.map(s => s.ip), 1);
  const maxClosed = Math.max(...scFiltered.map(s => s.closed), 1);
  const scColumns: SortableColumn<SCData>[] = [{
    key: 'n',
    label: 'SC Name',
    width: '18%',
    render: p => <span className="name-link" data-id="element-1065" data-mp="1jf92mc7ydw6p">
          {p.n}
          {longLeaveNames.has(p.n.toLowerCase()) && <span className="badge b-amber" style={{
        marginLeft: 4,
        fontSize: 8
      }} title="On long leave this year" data-id="element-1066" data-mp="3g8cael4mytei">
              🌴 long leave yr
            </span>}
          <span className={`chev${expandedSC === p.n ? ' open' : ''}`} data-id="element-1067" data-mp="sbdk8hwg1hd5">▾</span>
        </span>
  }, {
    key: 'ip',
    label: 'In-progress Req',
    width: '14%',
    sortValue: p => p.ip,
    render: p => <>
          <div className="pbg" style={{
        width: 70
      }} data-id="element-1068" data-mp="11dd19a7gklg3">
            <div className="pf" style={{
          width: `${Math.round(p.ip / maxIP * 100)}%`
        }} data-id="element-1069" data-mp="wdqot2zgf98c" />
          </div>
          <strong data-id="element-1070" data-mp="1bahshog5v73r">{p.ip}</strong>
        </>
  }, {
    key: 'closed',
    label: 'Closed Req',
    width: '14%',
    sortValue: p => p.closed,
    render: p => <>
          <div className="pbg" style={{
        width: 70
      }} data-id="element-1071" data-mp="1k31sas86vhq6">
            <div className="pf" style={{
          width: `${Math.round(p.closed / maxClosed * 100)}%`,
          background: '#34d399'
        }} data-id="element-1072" data-mp="1k2jonaj76188" />
          </div>
          <strong data-id="element-1073" data-mp="1g3u5t2fe3rh7">{p.closed}</strong>
        </>
  }, {
    key: 'total',
    label: 'Total Req',
    width: '10%',
    sortValue: p => p.ip + p.closed,
    render: p => <strong data-id="element-1074" data-mp="1p3zbn84xqht9">{p.ip + p.closed}</strong>
  }, {
    key: 'onhold',
    label: 'On Hold Req',
    width: '10%',
    sortValue: p => p.onhold,
    render: p => <strong data-id="element-1075" data-mp="2dnkg55pvmyzg">{p.onhold}</strong>
  }, {
    key: 'tpCount',
    label: 'Touchpoints',
    width: '10%',
    sortValue: p => tpSCMap.get(p.n)?.count ?? 0,
    render: p => {
      const count = tpSCMap.get(p.n)?.count ?? 0;
      return <span style={{
        color: count > 0 ? '#3B82F6' : '#8892a0',
        fontWeight: count > 0 ? 600 : 400
      }} data-id="element-1076" data-mp="23na7lyesefqm">{count}</span>;
    }
  },
  // {
  //   key: 'gnarr', label: 'GNARR (IP)', width: '8%',
  //   sortValue: p => p.gnarr,
  //   render: p => <>{fmtG(p.gnarr)}</>,
  // },
  // {
  //   key: 'closedGnarr', label: 'GNARR (Closed)', width: '8%',
  //   sortValue: p => p.closedGnarr,
  //   render: p => <>{fmtG(p.closedGnarr)}</>,
  // },
  {
    key: 'totalGnarr',
    label: 'GNARR (Closed + In-Prog Req)',
    width: '14%',
    sortValue: p => p.gnarr + p.closedGnarr,
    render: p => <strong data-id="element-1077" data-mp="1aze9qqdhehs9">{fmtG(p.gnarr + p.closedGnarr)}</strong>
  }, {
    key: 'avgAge',
    label: 'Avg age (Cls Req)',
    width: '10%',
    sortValue: p => p.avgAge,
    render: p => <span style={{
      color: p.avgAge > 90 ? '#f87171' : p.avgAge > 40 ? '#d4a017' : '#344054',
      fontWeight: p.avgAge > 40 ? 600 : 400
    }} data-id="element-1078" data-mp="yyeo9hgbfnnu">
          {p.avgAge}d
        </span>
  }, {
    key: 'med',
    label: 'Median age (Cls Req)',
    width: '10%',
    sortValue: p => p.med,
    render: p => <>{p.med}d</>
  }];
  const scTotalIP = scFiltered.reduce((s, p) => s + p.ip, 0);
  const scTotalClosed = scFiltered.reduce((s, p) => s + p.closed, 0);
  const scTotalOnHold = scFiltered.reduce((s, p) => s + p.onhold, 0);
  // Deduped by opp ID across SC rows (an opp shared by several SCs, or both In-Progress and Closed, counts once).
  const {
    ip: scTotalGnarr,
    closed: scTotalClosedGnarr,
    total: scTotalGnarrCombined
  } = sumScTotalsGnarr(scFiltered);
  const scTotalTp = scFiltered.reduce((s, p) => s + (tpSCMap.get(p.n)?.count ?? 0), 0);
  // Weighted by each SC's closed count — averaging per-SC averages directly would
  // ignore that SCs with more closed requests should count for more.
  const scWeightedAvgAge = scTotalClosed > 0 ? Math.round(scFiltered.reduce((s, p) => s + p.avgAge * p.closed, 0) / scTotalClosed) : 0;
  const scWeightedMedAge = scTotalClosed > 0 ? Math.round(scFiltered.reduce((s, p) => s + p.med * p.closed, 0) / scTotalClosed) : 0;
  const scFooterCellStyle: React.CSSProperties = {
    fontWeight: 700,
    background: '#fafbfc',
    borderTop: '2px solid #e8eaf0'
  };
  const scFooterRow = <tr data-id="element-1079" data-mp="1z5js08qx526i">
      <td style={scFooterCellStyle} data-id="element-1080" data-mp="1z5thlv81x7bo">Total</td>
      <td style={scFooterCellStyle} data-id="element-1081" data-mp="1z85y03y5v9mm">{scTotalIP}</td>
      <td style={scFooterCellStyle} data-id="element-1082" data-mp="245fvm6vperlj">{scTotalClosed}</td>
      <td style={scFooterCellStyle} data-id="element-1083" data-mp="242tcrb3d4po0">{scTotalIP + scTotalClosed}</td>
      <td style={scFooterCellStyle} data-id="element-1084" data-mp="3akgkjsmba9im">{scTotalOnHold}</td>
      <td style={scFooterCellStyle} data-id="element-1085" data-mp="3fk2vtwikxfos">{scTotalTp}</td>
      <td style={scFooterCellStyle} data-id="element-1086" data-mp="3firue3clwk0u">{fmtG(scTotalGnarrCombined)}</td>
      <td style={scFooterCellStyle} data-id="element-1087" data-mp="2pve664h18f7j">{scWeightedAvgAge}d</td>
      <td style={scFooterCellStyle} data-id="element-1088" data-mp="2kx4cxzevbook">{scWeightedMedAge}d</td>
      
      {/* 
       <td style={scFooterCellStyle}>{fmtG(scTotalClosedGnarr)}</td>
       <td style={scFooterCellStyle}>{fmtG(scTotalGnarr)}</td> */}
      
    </tr>;
  const handleScSortedDataChange = useCallback((rows: SCData[]) => setScSortedRows(rows), []);
  const handleExportScCsv = useCallback(() => {
    const csvEscape = (v: string | number) => {
      const s = String(v);
      return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
    };
    const header = ['SC Name', 'In-progress', 'Closed', 'Total Requests', 'On Hold Req', 'Avg Age (Closed)', 'Median Age (Closed)', 'GNARR (IP)', 'GNARR (Closed)', 'Total GNARR', 'Touchpoints'];
    const rows = scSortedRows.map(p => [p.n, p.ip, p.closed, p.ip + p.closed, p.onhold, p.avgAge, p.med, p.gnarr, p.closedGnarr, p.gnarr + p.closedGnarr, tpSCMap.get(p.n)?.count ?? 0]);
    rows.push(['Total', scTotalIP, scTotalClosed, scTotalIP + scTotalClosed, scTotalOnHold, scWeightedAvgAge, scWeightedMedAge, scTotalGnarr, scTotalClosedGnarr, scTotalGnarrCombined, scTotalTp]);
    const csv = [header, ...rows].map(r => r.map(csvEscape).join(',')).join('\n');
    const blob = new Blob([csv], {
      type: 'text/csv'
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sc-workload_${state.r}_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [scSortedRows, tpSCMap, scTotalIP, scTotalClosed, scTotalOnHold, scWeightedAvgAge, scWeightedMedAge, scTotalGnarr, scTotalClosedGnarr, scTotalGnarrCombined, scTotalTp, state.r]);
  return <div data-id="element-1089" data-mp="2tdv5j28ue4z9">
      {/* Metrics */}
      <div className="mrow" style={{
      gridTemplateColumns: 'repeat(5, 1fr)'
    }} data-id="element-1090" data-mp="2tctjk2qafy2r">
        <MetricCard label="In-progress requests" value={allIP.length} sub={`${scs} SCs active`} colorClass="c-blue" data-id="element-1091" data-mp="2xghtut6b17rp" />
        <MetricCard label="Total Requests / person" value={scs ? +(allForGnarr.length / scs).toFixed(1) : 0} sub="across active SCs" colorClass="c-purple" data-id="element-1092" data-mp="3p4ktdkqs7h08" />
        <MedianAgeCard ages={ages} data-id="element-1093" data-mp="3g1tf7d6iu74w" />
        <GnarrInfluencedCard value={oppGnarr} colorClass="c-green" data-id="element-1094" data-mp="3g34ry2hy2p85" />
        <GnarrInfluencedCard value={oppGnarrTotal} colorClass="c-green" label="GNARR (Total) Influenced" data-id="element-1095" data-mp="3b49z07q9jkab" />
      </div>

      {/* SC workload table */}
      <SectionHeader title={<>SC workload — requests / touchpoints (click row to expand){closedMode && <TouchpointCreatedHint data-id="element-1097" data-mp="7ow6rfhr50q0" />}</>} data-id="element-1096" data-mp="bqhk11y77h0q" />
      <div className="tw" data-id="element-1098" data-mp="2vjix7kwcxyb">
        <div style={{
        padding: '8px 12px 0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }} data-id="element-1099" data-mp="2urzsw8hksox">
          <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8
        }} data-id="element-1100" data-mp="3u2496p34o1ua">
            <input type="text" placeholder="Search SC name…" value={scSearch} onChange={e => {
            setScSearch(e.target.value);
            if (e.target.value) setScSelected([]);
            setExpandedSC(null);
          }} style={{
            fontSize: 10,
            padding: '4px 8px',
            border: '1px solid #d0d5dd',
            borderRadius: 5,
            outline: 'none',
            width: 160,
            color: '#344054',
            background: '#fff'
          }} data-id="element-1101" data-mp="vd5r0czbhgf1" />
            <div ref={scDropdownRef} style={{
            position: 'relative',
            zIndex: scDropdownOpen ? 10 : undefined
          }} data-id="element-1102" data-mp="1bu8iac4uc67q">
              <button ref={scDropdownBtnRef} onClick={() => {
              if (!scDropdownOpen && scDropdownBtnRef.current) {
                const rect = scDropdownBtnRef.current.getBoundingClientRect();
                setScDropdownPos({
                  top: rect.bottom + 4,
                  left: rect.left
                });
              }
              setScDropdownOpen(!scDropdownOpen);
            }} style={{
              fontSize: 10,
              padding: '4px 8px',
              border: '1px solid #d0d5dd',
              borderRadius: 5,
              outline: 'none',
              color: '#344054',
              background: '#fff',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 4
            }} data-id="element-1103" data-mp="1lur95qutomz5">
                {effectiveScSelected.length > 0 ? `Solution Consultants (${effectiveScSelected.length})` : 'Solution Consultants ▾'}
              </button>
              {scDropdownOpen && <div style={{
              position: 'fixed',
              top: scDropdownPos.top,
              left: scDropdownPos.left,
              zIndex: 1000,
              background: '#fff',
              border: '1px solid #d0d5dd',
              borderRadius: 5,
              boxShadow: '0 2px 6px rgba(0,0,0,.12)',
              minWidth: 160,
              padding: '4px 0',
              maxHeight: 260,
              overflowY: 'auto'
            }} data-id="element-1104" data-mp="3ohi8ft8sja48">
                  <div onClick={() => {
                setScSelected(effectiveScSelected.length === scOptions.length ? [] : [...scOptions]);
                setScSearch('');
                setExpandedSC(null);
              }} style={{
                padding: '4px 10px',
                fontSize: 10,
                cursor: 'pointer',
                color: '#667085',
                borderBottom: '1px solid #eaecf0'
              }} data-id="element-1105" data-mp="b22h1bm3suhj">
                    {effectiveScSelected.length === scOptions.length ? 'Deselect all' : 'Select all'}
                  </div>
                  {scOptions.map(n => <label key={n} style={{
                padding: '4px 10px',
                fontSize: 10,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6
              }} onClick={e => e.stopPropagation()} data-id="element-1106" data-mp="18v22gcr90a9q">
                      <input type="checkbox" checked={effectiveScSelected.includes(n)} onChange={() => {
                  setScSelected(prev => prev.includes(n) ? prev.filter(x => x !== n) : [...prev, n]);
                  setScSearch('');
                  setExpandedSC(null);
                }} data-id="element-1107" data-mp="z3fmk33ytn0n" />
                      {n}
                    </label>)}
                </div>}
            </div>
            {(scSearch || effectiveScSelected.length > 0) && <button onClick={() => {
            setScSearch('');
            setScSelected([]);
            setScDropdownOpen(false);
            setExpandedSC(null);
          }} title="Clear SC filter" aria-label="Clear SC filter" style={{
            fontSize: 11,
            lineHeight: 1,
            padding: '4px 7px',
            border: '1px solid #d0d5dd',
            borderRadius: 5,
            outline: 'none',
            color: '#667085',
            background: '#fff',
            cursor: 'pointer'
          }} data-id="element-1108" data-mp="3canixketejo8">
                ✕
              </button>}
            <div ref={productDropdownRef} style={{
            position: 'relative',
            zIndex: productDropdownOpen ? 10 : undefined
          }} data-id="element-1109" data-mp="rjn2ne85227l">
              <button ref={productDropdownBtnRef} onClick={() => {
              if (!productDropdownOpen && productDropdownBtnRef.current) {
                const rect = productDropdownBtnRef.current.getBoundingClientRect();
                setProductDropdownPos({
                  top: rect.bottom + 4,
                  left: rect.left
                });
              }
              setProductDropdownOpen(!productDropdownOpen);
            }} style={{
              fontSize: 10,
              padding: '4px 8px',
              border: '1px solid #d0d5dd',
              borderRadius: 5,
              outline: 'none',
              color: '#344054',
              background: '#fff',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 4
            }} data-id="element-1110" data-mp="275gygeu9dhx9">
                {productSelected.length > 0 ? `Products (${productSelected.length})` : 'Products ▾'}
              </button>
              {productDropdownOpen && <div style={{
              position: 'fixed',
              top: productDropdownPos.top,
              left: productDropdownPos.left,
              zIndex: 1000,
              background: '#fff',
              border: '1px solid #d0d5dd',
              borderRadius: 5,
              boxShadow: '0 2px 6px rgba(0,0,0,.12)',
              minWidth: 220,
              maxWidth: 360,
              paddingBottom: 4,
              maxHeight: 300,
              overflowY: 'auto'
            }} data-id="element-1111" data-mp="16vz5qqfhcss1">
                  <div style={{
                position: 'sticky',
                top: 0,
                background: '#fff',
                padding: '6px 8px',
                borderBottom: '1px solid #eaecf0'
              }} data-id="element-1112" data-mp="o7u5vu1y1ryi">
                    <input type="text" autoFocus placeholder="Search products…" value={productSearch} onChange={e => setProductSearch(e.target.value)} style={{
                  fontSize: 10,
                  padding: '4px 8px',
                  border: '1px solid #d0d5dd',
                  borderRadius: 5,
                  outline: 'none',
                  width: '100%',
                  boxSizing: 'border-box',
                  color: '#344054',
                  background: '#fff'
                }} data-id="element-1113" data-mp="y4gpkfijacyy" />
                  </div>
                  {visibleProducts.length === 0 ? <div style={{
                padding: '6px 10px',
                fontSize: 10,
                color: '#8892a0',
                fontStyle: 'italic'
              }} data-id="element-1114" data-mp="2enejfxve1nvb">No matching products</div> : <>
                      <div onClick={() => {
                  setProductSelected(prev => allVisibleProductsSelected ? prev.filter(p => !visibleProducts.includes(p)) : [...new Set([...prev, ...visibleProducts])]);
                  setExpandedSC(null);
                }} style={{
                  padding: '4px 10px',
                  fontSize: 10,
                  cursor: 'pointer',
                  color: '#667085',
                  borderBottom: '1px solid #eaecf0'
                }} data-id="element-1115" data-mp="24qjkjdv7dlgi">
                        {allVisibleProductsSelected ? 'Deselect all' : 'Select all'}
                      </div>
                      {visibleProducts.map(p => <label key={p} style={{
                  padding: '4px 10px',
                  fontSize: 10,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6
                }} onClick={e => e.stopPropagation()} data-id="element-1116" data-mp="1c9tmojdwvnaa">
                          <input type="checkbox" checked={productSelected.includes(p)} onChange={() => {
                    setProductSelected(prev => prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p]);
                    setExpandedSC(null);
                  }} data-id="element-1117" data-mp="1q5nsgtje1wj6" />
                          {p}
                        </label>)}
                    </>}
                </div>}
            </div>
            {productSelected.length > 0 && <button onClick={() => {
            setProductSelected([]);
            setProductDropdownOpen(false);
            setExpandedSC(null);
          }} title="Clear product filter" aria-label="Clear product filter" style={{
            fontSize: 11,
            lineHeight: 1,
            padding: '4px 7px',
            border: '1px solid #d0d5dd',
            borderRadius: 5,
            outline: 'none',
            color: '#667085',
            background: '#fff',
            cursor: 'pointer'
          }} data-id="element-1118" data-mp="3tin3c73mk0i3">
                ✕
              </button>}
          </div>
          <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10
        }} data-id="element-1119" data-mp="14rvqfgkqq8p0">
            <label style={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            fontSize: 10,
            color: '#344054',
            cursor: 'pointer'
          }} data-id="element-1120" data-mp="ztkg74qj9w69">
              <input type="checkbox" checked={hideInactive} onChange={e => {
              setHideInactive(e.target.checked);
              setExpandedSC(null);
            }} data-id="element-1121" data-mp="2td2nimktz6ob" />
              Hide inactive
            </label>
            <button onClick={handleExportScCsv} style={{
            fontSize: 10,
            padding: '4px 10px',
            border: '1px solid #d0d5dd',
            borderRadius: 5,
            outline: 'none',
            color: '#344054',
            background: '#fff',
            cursor: 'pointer'
          }} data-id="element-1122" data-mp="381h03p81u9i2">
              Export CSV
            </button>
          </div>
        </div>
        <SortableTable columns={scColumns} data={scFiltered} rowKey={p => p.n} rowStyle={p => hiddenScNames.has(p.n.toLowerCase()) ? {
        background: '#f2f4f7',
        color: '#8892a0'
      } : undefined} footer={scFooterRow} onSortedDataChange={handleScSortedDataChange} onRowClick={p => {
        if (expandedSC === p.n) {
          setExpandedSC(null);
        } else {
          setExpandedSC(p.n);
          setExpandedViewMode('deals');
        }
      }} renderExpandedRow={p => expandedSC === p.n ? <tr key={`exp-${p.n}`} data-id="element-1124" data-mp="1ae4yyo5bbd90">
              <td colSpan={10} style={{
          padding: 0
        }} data-id="element-1125" data-mp="1af6bkx1z87su">
                <div className="exp-inner" data-id="element-1126" data-mp="2813ngblupevt">
                  <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              marginBottom: 6
            }} data-id="element-1127" data-mp="280vakjpqa9km">
                    <div style={{
                fontSize: 9,
                fontWeight: 700,
                color: '#667085',
                textTransform: 'uppercase',
                letterSpacing: '.05em',
                flex: 1
              }} data-id="element-1128" data-mp="232tqbvgkg47k">
                      {expandedViewMode === 'deals' ? <>In-progress book — {p.n} ({p.ip} requests · {fmtG(p.gnarr)} GNARR)</> : <>Touchpoints — {p.n} ({tpSCMap.get(p.n)?.count ?? 0} touchpoints)</>}
                    </div>
                    <div style={{
                display: 'flex',
                border: '1px solid #d0d5dd',
                borderRadius: 5,
                overflow: 'hidden'
              }} data-id="element-1129" data-mp="3c2p878bttcuq">
                      <button onClick={() => setExpandedViewMode('deals')} style={{
                  fontSize: 9,
                  padding: '3px 10px',
                  border: 'none',
                  cursor: 'pointer',
                  background: expandedViewMode === 'deals' ? '#3B82F6' : '#fff',
                  color: expandedViewMode === 'deals' ? '#fff' : '#344054',
                  fontWeight: expandedViewMode === 'deals' ? 600 : 400
                }} data-id="element-1130" data-mp="3744elrr2xrf9">
                        Requests
                      </button>
                      <button onClick={() => setExpandedViewMode('touchpoints')} style={{
                  fontSize: 9,
                  padding: '3px 10px',
                  border: 'none',
                  borderLeft: '1px solid #d0d5dd',
                  cursor: 'pointer',
                  background: expandedViewMode === 'touchpoints' ? '#3B82F6' : '#fff',
                  color: expandedViewMode === 'touchpoints' ? '#fff' : '#344054',
                  fontWeight: expandedViewMode === 'touchpoints' ? 600 : 400
                }} data-id="element-1131" data-mp="3mcko7tduxef">
                        Touchpoints
                      </button>
                    </div>
                    <ViewSwitcher active={dealView} onChange={setDealView} data-id="element-1132" data-mp="25qcnehqq4v0" />
                  </div>
                  {expandedViewMode === 'deals' ? <>
                      <DealExpandedView requests={p.requests} mode={dealView} data-id="element-1133" data-mp="657jab8wl81r" />
                      {(onHoldBySC.get(p.n)?.length ?? 0) > 0 && <>
                          <div style={{
                  fontSize: 9,
                  fontWeight: 700,
                  color: '#d97706',
                  margin: '12px 0 6px',
                  textTransform: 'uppercase',
                  letterSpacing: '.05em'
                }} data-id="element-1134" data-mp="3tcjm15motaam">
                            On hold — {p.n} ({onHoldBySC.get(p.n)?.length ?? 0} requests)
                          </div>
                          <DealExpandedView requests={onHoldBySC.get(p.n) ?? []} mode={dealView} data-id="element-1135" data-mp="1q499idybjizp" />
                        </>}
                      {p.closedRequests.length > 0 && <>
                          <div style={{
                  fontSize: 9,
                  fontWeight: 700,
                  color: '#059669',
                  margin: '12px 0 6px',
                  textTransform: 'uppercase',
                  letterSpacing: '.05em'
                }} data-id="element-1136" data-mp="1g7edq2tfjwp4">
                            Closed — {p.n} ({p.closed} requests)
                          </div>
                          <DealExpandedView requests={p.closedRequests} mode={dealView} data-id="element-1137" data-mp="23w7bm4tm4emd" />
                        </>}
                    </> : <TouchpointExpandedView touchpoints={tpSCMap.get(p.n)?.touchpoints ?? []} mode={dealView} data-id="element-1138" data-mp="1u5upw9a2afen" />}
                </div>
              </td>
            </tr> : null} data-id="element-1123" data-mp="3qzt6j37kt59y" />
      </div>

      {/* Solution × SC matrix */}
      <SectionHeader title="Solution × SC matrix — product concentration" data-id="element-1139" data-mp="2mrxh420r7t50" />
      <div className="tw" data-id="element-1140" data-mp="2v47g1nktxvrt">
        <div className="sub" data-id="element-1141" data-mp="2z3zu2fszsu4k">
          In-progress deals by product bucket per SC. Reveals single-threading and coverage gaps.
        </div>
        <div style={{
        overflowX: 'auto'
      }} data-id="element-1142" data-mp="3421eb425mzhm">
          <table className="tbl" id="od-matrix-tbl" style={{
          minWidth: 500
        }} data-id="element-1143" data-mp="345fd62bm0qkb">
            <SCMatrix region={state.r} sub={state.sub} cqState={state} data={requests} data-id="element-1144" data-mp="393gyyv0f73zh" />
          </table>
        </div>
      </div>

      {/* Touchpoints */}
      <SectionHeader title={<>Touchpoints{closedMode && <TouchpointCreatedHint data-id="element-1146" data-mp="3i0r8obxlal2a" />}</>} data-id="element-1145" data-mp="3i22cucuy5utx" />
      <div style={{
      padding: '8px 18px 0',
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      flexWrap: 'wrap'
    }} data-id="element-1147" data-mp="3mt28zfw8s8vd">
        <input type="text" placeholder="Search ID…" value={tpSearchId} onChange={e => setTpSearchId(e.target.value)} style={{
        fontSize: 10,
        padding: '4px 8px',
        border: '1px solid #d0d5dd',
        borderRadius: 5,
        outline: 'none',
        width: 110,
        color: '#344054',
        background: '#fff'
      }} data-id="element-1148" data-mp="3rswzhic3uwfw" />
        <input type="text" placeholder="Search name…" value={tpSearchName} onChange={e => setTpSearchName(e.target.value)} style={{
        fontSize: 10,
        padding: '4px 8px',
        border: '1px solid #d0d5dd',
        borderRadius: 5,
        outline: 'none',
        width: 140,
        color: '#344054',
        background: '#fff'
      }} data-id="element-1149" data-mp="xdqxjfiwkr3t" />
        <input type="text" placeholder="Search account…" value={tpSearchAccount} onChange={e => setTpSearchAccount(e.target.value)} style={{
        fontSize: 10,
        padding: '4px 8px',
        border: '1px solid #d0d5dd',
        borderRadius: 5,
        outline: 'none',
        width: 140,
        color: '#344054',
        background: '#fff'
      }} data-id="element-1150" data-mp="jg2e8c4hhs0v" />
        <input type="text" placeholder="Search SC…" value={tpSearchSc} onChange={e => setTpSearchSc(e.target.value)} style={{
        fontSize: 10,
        padding: '4px 8px',
        border: '1px solid #d0d5dd',
        borderRadius: 5,
        outline: 'none',
        width: 140,
        color: '#344054',
        background: '#fff'
      }} data-id="element-1151" data-mp="23rzzgetzbjm8" />
        <div style={{
        position: 'relative'
      }} data-id="element-1152" data-mp="1xemco7icww6f">
          <button onClick={() => setTpTypeDropdownOpen(!tpTypeDropdownOpen)} style={{
          fontSize: 10,
          padding: '4px 8px',
          border: '1px solid #d0d5dd',
          borderRadius: 5,
          outline: 'none',
          color: '#344054',
          background: '#fff',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 4
        }} data-id="element-1153" data-mp="1xfxfo53z9zwh">
            {tpTypeFilter.length > 0 ? `Type (${tpTypeFilter.length})` : 'Type ▾'}
          </button>
          {tpTypeDropdownOpen && <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          marginTop: 4,
          zIndex: 50,
          background: '#fff',
          border: '1px solid #d0d5dd',
          borderRadius: 5,
          boxShadow: '0 2px 6px rgba(0,0,0,.12)',
          minWidth: 160,
          padding: '4px 0'
        }} data-id="element-1154" data-mp="391clsejx3cg7">
              <div onClick={() => setTpTypeFilter(tpTypeFilter.length === tpTypes.length ? [] : [...tpTypes])} style={{
            padding: '4px 10px',
            fontSize: 10,
            cursor: 'pointer',
            color: '#667085',
            borderBottom: '1px solid #eaecf0'
          }} data-id="element-1155" data-mp="3ioowhq3qm8hd">
                {tpTypeFilter.length === tpTypes.length ? 'Deselect all' : 'Select all'}
              </div>
              {tpTypes.map(t => <label key={t} style={{
            padding: '4px 10px',
            fontSize: 10,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 6
          }} onClick={e => e.stopPropagation()} data-id="element-1156" data-mp="2zxp5kjcomolg">
                  <input type="checkbox" checked={tpTypeFilter.includes(t)} onChange={() => {
              setTpTypeFilter(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t]);
            }} data-id="element-1157" data-mp="2qvfspmtxyj4w" />
                  {t}
                </label>)}
            </div>}
        </div>
        <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        fontSize: 10,
        color: '#667085'
      }} data-id="element-1158" data-mp="16f8bncmvicoa">
          <span data-id="element-1159" data-mp="1ag1y01ko3jf2">Entry</span>
          <input type="date" value={tpEntryFrom} onChange={e => setTpEntryFrom(e.target.value)} style={{
          fontSize: 10,
          padding: '3px 6px',
          border: '1px solid #d0d5dd',
          borderRadius: 5,
          outline: 'none',
          color: '#344054',
          background: '#fff'
        }} data-id="element-1160" data-mp="1agac1wsj0uru" />
          <span data-id="element-1161" data-mp="rhz4pal370bg">–</span>
          <input type="date" value={tpEntryTo} onChange={e => setTpEntryTo(e.target.value)} style={{
          fontSize: 10,
          padding: '3px 6px',
          border: '1px solid #d0d5dd',
          borderRadius: 5,
          outline: 'none',
          color: '#344054',
          background: '#fff'
        }} data-id="element-1162" data-mp="rihbv29aeyy5" />
        </div>
      </div>
      <div className="tw" data-id="element-1163" data-mp="2jkwweazu5549">
        <SortableTable columns={[{
        key: 'opp_id',
        label: 'ID',
        width: '10%',
        sortValue: (t: Touchpoint) => t.opp_id,
        render: (t: Touchpoint) => <span style={{
          fontSize: 9,
          color: '#8892a0'
        }} data-id="element-1165" data-mp="2tj2xwrhqc98l">{t.opp_id}</span>
      }, {
        key: 'tp_name',
        label: 'Touchpoints Name',
        width: '18%',
        sortValue: (t: Touchpoint) => t.tp_name,
        render: (t: Touchpoint) => <span title={t.tp_name} data-id="element-1166" data-mp="32kav168if413">{t.tp_name.substring(0, 24)}</span>
      }, {
        key: 'acc_name',
        label: 'Account',
        width: '14%',
        sortValue: (t: Touchpoint) => t.acc_name,
        render: (t: Touchpoint) => <span title={t.acc_name} data-id="element-1167" data-mp="3c9av6syr05gv">{t.acc_name.substring(0, 18)}</span>
      }, {
        key: 'user_sc_name',
        label: 'SC',
        width: '10%',
        sortValue: (t: Touchpoint) => t.user_sc_name,
        render: (t: Touchpoint) => <ScNamesCell names={t.user_sc_name} data-id="element-1168" data-mp="3m91q2vr80mhh" />
      }, {
        key: 'tp_type',
        label: 'Type',
        width: '12%',
        sortValue: (t: Touchpoint) => t.tp_type || '',
        render: (t: Touchpoint) => t.tp_type ? <span className="badge b-blue" style={{
          fontSize: 8
        }} data-id="element-1169" data-mp="dog8woel99ln">{t.tp_type}</span> : <span style={{
          color: '#d1d5db'
        }} data-id="element-1170" data-mp="3vvvxwrtvi61r">—</span>
      }, {
        key: 'tp_entry',
        label: 'Entry',
        width: '12%',
        sortValue: (t: Touchpoint) => ddmmyyToSortValue(t.tp_entry),
        render: (t: Touchpoint) => <span style={{
          fontSize: 9,
          color: '#667085'
        }} data-id="element-1171" data-mp="3m4rhrosgof5c">{t.tp_entry}</span>
      }, {
        key: 'tp_solution_comments',
        label: 'Comments',
        width: '16%',
        sortValue: (t: Touchpoint) => t.tp_solution_comments || '',
        render: (t: Touchpoint) => <span title={t.tp_solution_comments} data-id="element-1172" data-mp="3d31u8u1pa0cu">{t.tp_solution_comments ? t.tp_solution_comments.substring(0, 35) + (t.tp_solution_comments.length > 35 ? '…' : '') : '—'}</span>
      }]} data={touchpointsFiltered} rowKey={(t: Touchpoint) => t.tp_id} defaultSort={{
        key: 'entry',
        dir: 'desc'
      }} pageSize={25} data-id="element-1164" data-mp="2jlosro19b6vl" />
      </div>
    </div>;
}